import {
	Badge,
	Box,
	CircularProgress,
	Divider,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import type { SxProps } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo } from "react";
import {
	type GroupedDelayAsset,
	getDelayAssetsGrouped,
} from "../repositories/DelayAssetRepository";
import { getAssetColor, toSlug } from "../utils/assets";

type AreaIds = {
	saws: number;
	welds: number;
};

type Props = {
	value: string | null;
	onChange: (assetName: string | null, assetId: number | null) => void;
	pendingCounts?: Record<number, number>;
	areaIds?: AreaIds;
};

const getButtonSx = (color: string): SxProps => ({
	textTransform: "none",
	px: 2,
	"&.Mui-selected, &.Mui-selected:hover": {
		backgroundColor: color,
		borderColor: color,
		color: "#fff",
		fontWeight: 500,
	},
});

export const useDelayAssetsGrouped = () =>
	useQuery<GroupedDelayAsset[], Error>({
		queryKey: ["delayAssetsGrouped"],
		queryFn: () => getDelayAssetsGrouped(),
	});

export const AssetButtonGroup = ({
	value,
	onChange,
	pendingCounts,
	areaIds,
}: Props) => {
	const { data: assets, isLoading } = useDelayAssetsGrouped();

	// Two rows: saws on top; welds, standalone lines and clip presses below
	const rows = useMemo(() => {
		if (!assets) return [];

		const map = new Map<number | null, GroupedDelayAsset[]>();
		for (const asset of assets) {
			const key = asset.ParentAssetId;
			const group = map.get(key);
			if (group) {
				group.push(asset);
			} else {
				map.set(key, [asset]);
			}
		}

		const groups = Array.from(map.entries()).map(([parentId, group]) => ({
			parentId,
			group: group.sort((a, b) => a.AssetName.localeCompare(b.AssetName)),
		}));

		const isSaw = (parentId: number | null) =>
			areaIds?.saws != null && parentId === areaIds.saws;
		const bottomOrder = (parentId: number | null) =>
			areaIds?.welds != null && parentId === areaIds.welds ? 0 : 1;

		const top = groups.filter((g) => isSaw(g.parentId)).map((g) => g.group);
		const bottom = groups
			.filter((g) => !isSaw(g.parentId))
			.sort(
				(a, b) =>
					bottomOrder(a.parentId) - bottomOrder(b.parentId) ||
					(a.parentId ?? 0) - (b.parentId ?? 0),
			)
			.map((g) => g.group);

		return [top, bottom].filter((row) => row.length > 0);
	}, [assets, areaIds]);

	if (isLoading) {
		return <CircularProgress size={24} />;
	}

	return (
		<Stack spacing={1}>
			{rows.map((rowGroups) => (
				<Box
					key={rowGroups[0][0].ParentAssetId}
					sx={{
						display: "flex",
						gap: 1,
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					{rowGroups.map((group, index) => (
						<Fragment key={group[0].ParentAssetId}>
							{index > 0 && (
								<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
							)}
							<ToggleButtonGroup
								exclusive
								value={value}
								onChange={(_, slug) => {
									if (slug === null) return;
									const asset = assets?.find(
										(a) => toSlug(a.AssetName) === slug,
									);
									onChange(slug, asset?.AssetId ?? null);
								}}
								size="small"
							>
								{group.map((asset) => {
									const count = pendingCounts?.[asset.AssetId] ?? 0;
									return (
										<ToggleButton
											key={asset.AssetId}
											value={toSlug(asset.AssetName)}
											sx={getButtonSx(getAssetColor(asset))}
										>
											<Badge
												badgeContent={count}
												color="error"
												invisible={count === 0}
												sx={{
													"& .MuiBadge-badge": {
														top: -8,
														right: -8,
													},
												}}
											>
												{asset.AssetName}
											</Badge>
										</ToggleButton>
									);
								})}
							</ToggleButtonGroup>
						</Fragment>
					))}
				</Box>
			))}
		</Stack>
	);
};
